import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Trophy, Users, Euro, Calculator, Info, ChevronDown, ChevronUp, AlertTriangle, Shield } from 'lucide-react';
import { getFinancialSettings, getPayoutStructure, checkPayoutCompliance } from '../services/api';

// Default payout structure (fallback if API returns no data)
const defaultPayoutStructure = {
  '2-3': {
    winners: 1,
    payouts: [
      { position: 1, percentage: 100 }
    ]
  },
  '4-9': {
    winners: 2,
    payouts: [
      { position: 1, percentage: 65 },
      { position: 2, percentage: 35 }
    ]
  },
  '10-19': {
    winners: 5,
    payouts: [
      { position: 1, percentage: 32 },
      { position: 2, percentage: 23 },
      { position: 3, percentage: 17 },
      { position: 4, percentage: 15 },
      { position: 5, percentage: 13 }
    ]
  },
  '20-29': {
    winners: 7,
    payouts: [
      { position: 1, percentage: 30 },
      { position: 2, percentage: 21 },
      { position: 3, percentage: 14 },
      { position: 4, percentage: 10 },
      { position: 5, percentage: 9 },
      { position: 6, percentage: 8.5 },
      { position: 7, percentage: 7.5 }
    ]
  },
  '30-39': {
    winners: 8,
    payouts: [
      { position: 1, percentage: 30 },
      { position: 2, percentage: 20 },
      { position: 3, percentage: 14 },
      { position: 4, percentage: 10 },
      { position: 5, percentage: 8 },
      { position: 6, percentage: 7 },
      { position: 7, percentage: 6 },
      { position: 8, percentage: 5 }
    ]
  },
  '40-49': {
    winners: 10,
    payouts: [
      { position: 1, percentage: 28 },
      { position: 2, percentage: 18 },
      { position: 3, percentage: 12 },
      { position: 4, percentage: 9 },
      { position: 5, percentage: 7 },
      { position: 6, percentage: 6 },
      { position: 7, percentage: 5 },
      { position: 8, percentage: 5 },
      { position: 9, percentage: 5 },
      { position: 10, percentage: 5 }
    ]
  },
  '50-59': {
    winners: 12,
    payouts: [
      { position: 1, percentage: 26 },
      { position: 2, percentage: 17 },
      { position: 3, percentage: 11 },
      { position: 4, percentage: 8 },
      { position: 5, percentage: 6.5 },
      { position: 6, percentage: 5.5 },
      { position: 7, percentage: 5 },
      { position: 8, percentage: 4.5 },
      { position: 9, percentage: 4.5 },
      { position: 10, percentage: 4 },
      { position: 11, percentage: 4 },
      { position: 12, percentage: 4 }
    ]
  },
  '60-69': {
    winners: 14,
    payouts: [
      { position: 1, percentage: 24 },
      { position: 2, percentage: 16 },
      { position: 3, percentage: 10 },
      { position: 4, percentage: 7.5 },
      { position: 5, percentage: 6 },
      { position: 6, percentage: 5 },
      { position: 7, percentage: 4.5 },
      { position: 8, percentage: 4 },
      { position: 9, percentage: 4 },
      { position: 10, percentage: 4 },
      { position: 11, percentage: 3.5 },
      { position: 12, percentage: 3.5 },
      { position: 13, percentage: 3.5 },
      { position: 14, percentage: 4.5 }
    ]
  },
  '70-79': {
    winners: 16,
    payouts: [
      { position: 1, percentage: 22 },
      { position: 2, percentage: 14 },
      { position: 3, percentage: 9 },
      { position: 4, percentage: 7 },
      { position: 5, percentage: 5.5 },
      { position: 6, percentage: 4.5 },
      { position: 7, percentage: 4 },
      { position: 8, percentage: 4 },
      { position: 9, percentage: 3.5 },
      { position: 10, percentage: 3.5 },
      { position: 11, percentage: 3.5 },
      { position: 12, percentage: 3.5 },
      { position: 13, percentage: 3.5 },
      { position: 14, percentage: 3.5 },
      { position: 15, percentage: 3.5 },
      { position: 16, percentage: 5.5 }
    ]
  },
  '80-99': {
    winners: 20,
    payouts: [
      { position: 1, percentage: 18 },
      { position: 2, percentage: 12 },
      { position: 3, percentage: 8 },
      { position: 4, percentage: 6 },
      { position: 5, percentage: 5 },
      { position: 6, percentage: 4 },
      { position: 7, percentage: 3.5 },
      { position: 8, percentage: 3.5 },
      { position: 9, percentage: 3 },
      { position: 10, percentage: 3 },
      { position: 11, percentage: 3 },
      { position: 12, percentage: 3 },
      { position: 13, percentage: 3 },
      { position: 14, percentage: 3 },
      { position: 15, percentage: 3 },
      { position: 16, percentage: 3 },
      { position: 17, percentage: 3 },
      { position: 18, percentage: 3 },
      { position: 19, percentage: 3 },
      { position: 20, percentage: 5 }
    ]
  },
  '100-149': {
    winners: 43,
    payouts: [
      { position: 1, percentage: 18.1 },
      { position: 2, percentage: 12 },
      { position: 3, percentage: 7.1 },
      { position: 4, percentage: 4.75 },
      { position: 5, percentage: 4 },
      { position: 6, percentage: 3.5 },
      { position: 7, percentage: 3 },
      { position: 8, percentage: 2.3 },
      { position: 9, percentage: 2.2 },
      { position: 10, percentage: 1.9 },
      { position: 11, percentage: 1.9 },
      { position: 12, percentage: 1.75 },
      { position: 13, percentage: 1.6 },
      { position: 14, percentage: 1.6 },
      { position: 15, percentage: 1.5 },
      { position: 16, percentage: 1.5 },
      { position: 17, percentage: 1.5 },
      { position: 18, percentage: 1.5 },
      { position: 19, percentage: 1.4 },
      { position: 20, percentage: 1.4 },
      { position: 21, percentage: 1.4 },
      { position: 22, percentage: 1.4 },
      { position: 23, percentage: 1.3 },
      { position: 24, percentage: 1.3 },
      { position: 25, percentage: 1.3 },
      { position: 26, percentage: 1.3 },
      { position: 27, percentage: 1.17 },
      { position: 28, percentage: 1.17 },
      { position: 29, percentage: 1.05 },
      { position: 30, percentage: 1.05 },
      { position: 31, percentage: 1.05 },
      { position: 32, percentage: 1.05 },
      { position: 33, percentage: 0.95 },
      { position: 34, percentage: 0.95 },
      { position: 35, percentage: 0.95 },
      { position: 36, percentage: 0.95 },
      { position: 37, percentage: 0.88 },
      { position: 38, percentage: 0.88 },
      { position: 39, percentage: 0.88 },
      { position: 40, percentage: 0.88 },
      { position: 41, percentage: 0.81 },
      { position: 42, percentage: 0.81 },
      { position: 43, percentage: 0.81 }
    ]
  },
  '150-199': {
    winners: 50,
    payouts: [
      { position: 1, percentage: 10.38 },
      { position: 2, percentage: 8.05 },
      { position: 3, percentage: 6.24 },
      { position: 4, percentage: 4.84 },
      { position: 5, percentage: 3.75 },
      { position: 6, percentage: 2.91 },
      { position: 7, percentage: 2.26 },
      { position: 8, percentage: 1.75 },
      { position: 9, percentage: 1.36 },
      { position: 10, percentage: 1.05 },
      { position: 11, percentage: 0.82 },
      { position: 12, percentage: 0.82 },
      { position: 13, percentage: 0.82 },
      { position: 14, percentage: 0.82 },
      { position: 15, percentage: 0.82 },
      { position: 16, percentage: 0.82 },
      { position: 17, percentage: 0.82 },
      { position: 18, percentage: 0.82 },
      { position: 19, percentage: 0.82 },
      { position: 20, percentage: 0.82 },
      { position: 21, percentage: 0.82 },
      { position: 22, percentage: 0.82 },
      { position: 23, percentage: 0.82 },
      { position: 24, percentage: 0.82 },
      { position: 25, percentage: 0.82 },
      { position: 26, percentage: 0.82 },
      { position: 27, percentage: 0.82 },
      { position: 28, percentage: 0.82 },
      { position: 29, percentage: 0.82 },
      { position: 30, percentage: 0.82 },
      { position: 31, percentage: 0.82 },
      { position: 32, percentage: 0.82 },
      { position: 33, percentage: 0.82 },
      { position: 34, percentage: 0.82 },
      { position: 35, percentage: 0.82 },
      { position: 36, percentage: 0.82 },
      { position: 37, percentage: 0.82 },
      { position: 38, percentage: 0.82 },
      { position: 39, percentage: 0.82 },
      { position: 40, percentage: 0.82 },
      { position: 41, percentage: 0.82 },
      { position: 42, percentage: 0.82 },
      { position: 43, percentage: 0.82 },
      { position: 44, percentage: 0.82 },
      { position: 45, percentage: 0.82 },
      { position: 46, percentage: 0.82 },
      { position: 47, percentage: 0.82 },
      { position: 48, percentage: 0.82 },
      { position: 49, percentage: 0.82 },
      { position: 50, percentage: 0.73 }
    ]
  },
  '200-499': {
    winners: 70,
    payouts: [
      { position: 1, percentage: 5.34 },
      { position: 2, percentage: 4.48 },
      { position: 3, percentage: 3.76 },
      { position: 4, percentage: 3.15 },
      { position: 5, percentage: 2.65 },
      { position: 6, percentage: 2.22 },
      { position: 7, percentage: 1.86 },
      { position: 8, percentage: 1.56 },
      { position: 9, percentage: 1.31 },
      { position: 10, percentage: 1.10 },
      { position: 11, percentage: 0.92 },
      { position: 12, percentage: 0.77 },
      { position: 13, percentage: 0.65 },
      { position: 14, percentage: 0.54 },
      { position: 15, percentage: 0.54 },
      { position: 16, percentage: 0.54 },
      { position: 17, percentage: 0.54 },
      { position: 18, percentage: 0.54 },
      { position: 19, percentage: 0.54 },
      { position: 20, percentage: 0.54 },
      { position: 21, percentage: 0.54 },
      { position: 22, percentage: 0.54 },
      { position: 23, percentage: 0.54 },
      { position: 24, percentage: 0.54 },
      { position: 25, percentage: 0.54 },
      { position: 26, percentage: 0.54 },
      { position: 27, percentage: 0.54 },
      { position: 28, percentage: 0.54 },
      { position: 29, percentage: 0.54 },
      { position: 30, percentage: 0.54 },
      { position: 31, percentage: 0.54 },
      { position: 32, percentage: 0.54 },
      { position: 33, percentage: 0.54 },
      { position: 34, percentage: 0.54 },
      { position: 35, percentage: 0.54 },
      { position: 36, percentage: 0.54 },
      { position: 37, percentage: 0.54 },
      { position: 38, percentage: 0.54 },
      { position: 39, percentage: 0.54 },
      { position: 40, percentage: 0.54 },
      { position: 41, percentage: 0.54 },
      { position: 42, percentage: 0.54 },
      { position: 43, percentage: 0.54 },
      { position: 44, percentage: 0.54 },
      { position: 45, percentage: 0.54 },
      { position: 46, percentage: 0.54 },
      { position: 47, percentage: 0.54 },
      { position: 48, percentage: 0.54 },
      { position: 49, percentage: 0.54 },
      { position: 50, percentage: 0.54 },
      { position: 51, percentage: 0.54 },
      { position: 52, percentage: 0.54 },
      { position: 53, percentage: 0.54 },
      { position: 54, percentage: 0.54 },
      { position: 55, percentage: 0.54 },
      { position: 56, percentage: 0.54 },
      { position: 57, percentage: 0.54 },
      { position: 58, percentage: 0.54 },
      { position: 59, percentage: 0.54 },
      { position: 60, percentage: 0.54 },
      { position: 61, percentage: 0.54 },
      { position: 62, percentage: 0.54 },
      { position: 63, percentage: 0.54 },
      { position: 64, percentage: 0.54 },
      { position: 65, percentage: 0.54 },
      { position: 66, percentage: 0.54 },
      { position: 67, percentage: 0.54 },
      { position: 68, percentage: 0.54 },
      { position: 69, percentage: 0.54 },
      { position: 70, percentage: 0.36 }
    ]
  },
  '500-999': {
    winners: 131,
    payouts: Array.from({ length: 131 }, (_, i) => ({
      position: i + 1,
      percentage: i === 0 ? 2.48 : i < 10 ? (2.48 * Math.pow(0.75, i)).toFixed(2) : 0.18
    })).map((p, i, arr) => {
      // Normalize to 100%
      const total = arr.reduce((sum, item) => sum + parseFloat(item.percentage), 0);
      return { position: p.position, percentage: parseFloat((parseFloat(p.percentage) / total * 100).toFixed(2)) };
    })
  },
  '1000-1499': {
    winners: 200,
    payouts: Array.from({ length: 200 }, (_, i) => ({
      position: i + 1,
      percentage: i === 0 ? 1.41 : i < 15 ? (1.41 * Math.pow(0.82, i)).toFixed(2) : 0.14
    })).map((p, i, arr) => {
      const total = arr.reduce((sum, item) => sum + parseFloat(item.percentage), 0);
      return { position: p.position, percentage: parseFloat((parseFloat(p.percentage) / total * 100).toFixed(2)) };
    })
  },
  '1500-2000': {
    winners: 300,
    payouts: Array.from({ length: 300 }, (_, i) => ({
      position: i + 1,
      percentage: i === 0 ? 1.06 : i < 20 ? (1.06 * Math.pow(0.85, i)).toFixed(2) : 0.10
    })).map((p, i, arr) => {
      const total = arr.reduce((sum, item) => sum + parseFloat(item.percentage), 0);
      return { position: p.position, percentage: parseFloat((parseFloat(p.percentage) / total * 100).toFixed(2)) };
    })
  }
};

const Prijzen = () => {
  const [selectedParticipants, setSelectedParticipants] = useState(20);
  const [showFullTable, setShowFullTable] = useState(false);
  const [financialSettings, setFinancialSettings] = useState({
    cost_per_race: 1.0,
    admin_cost: 1.0,
    yearly_bonus: 0.0,
    races_count: 24
  });
  const [payoutStructure, setPayoutStructure] = useState(defaultPayoutStructure);
  const [loading, setLoading] = useState(true);
  const [complianceData, setComplianceData] = useState(null);
  const [checkingCompliance, setCheckingCompliance] = useState(false);
  
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Load financial settings and payout structure in parallel
        const [settings, payoutData] = await Promise.all([
          getFinancialSettings(2026),
          getPayoutStructure()
        ]);
        setFinancialSettings(settings);
        
        // Use API structure if available, otherwise use default
        if (payoutData?.structure) {
          setPayoutStructure(payoutData.structure);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  // Check compliance when participants or settings change
  useEffect(() => {
    const checkCompliance = async () => {
      if (loading) return;
      setCheckingCompliance(true);
      try {
        const result = await checkPayoutCompliance(
          selectedParticipants,
          financialSettings.cost_per_race,
          financialSettings.races_count
        );
        setComplianceData(result);
      } catch (error) {
        console.error('Error checking compliance:', error);
        setComplianceData(null);
      } finally {
        setCheckingCompliance(false);
      }
    };
    checkCompliance();
  }, [selectedParticipants, financialSettings, loading]);
  
  const COST_PER_RACE = financialSettings.cost_per_race;
  const RACES_2026 = financialSettings.races_count;
  const ADMIN_COST = financialSettings.admin_cost;
  const YEARLY_BONUS = financialSettings.yearly_bonus || 0;
  // Total entry per person: race costs + admin + yearly bonus (per user per season)
  const TOTAL_ENTRY = (COST_PER_RACE * RACES_2026) + ADMIN_COST + YEARLY_BONUS;
  const MAX_PAYOUT = 449;

  // Calculate prize pool (participants * €24, admin costs go to organization)
  const prizePool = selectedParticipants * COST_PER_RACE * RACES_2026;

  // Get the right payout structure based on participants
  const getPayoutCategory = (participants) => {
    if (participants <= 3) return '2-3';
    if (participants <= 9) return '4-9';
    if (participants <= 19) return '10-19';
    if (participants <= 29) return '20-29';
    if (participants <= 39) return '30-39';
    if (participants <= 49) return '40-49';
    if (participants <= 59) return '50-59';
    if (participants <= 69) return '60-69';
    if (participants <= 79) return '70-79';
    if (participants <= 99) return '80-99';
    if (participants <= 149) return '100-149';
    if (participants <= 199) return '150-199';
    if (participants <= 499) return '200-499';
    if (participants <= 999) return '500-999';
    if (participants <= 1499) return '1000-1499';
    return '1500-2000';
  };

  const currentCategory = getPayoutCategory(selectedParticipants);
  const currentStructure = payoutStructure[currentCategory];
  
  // Use proposed structure from compliance check if not compliant, otherwise use default
  const effectivePayouts = (complianceData && !complianceData.compliant && complianceData.proposed_structure?.payouts) 
    ? complianceData.proposed_structure.payouts 
    : (currentStructure?.payouts || []);
  const effectiveWinners = (complianceData && !complianceData.compliant && complianceData.proposed_winners) 
    ? complianceData.proposed_winners 
    : (currentStructure?.winners || 0);

  const getPositionLabel = (pos) => {
    if (pos === 1) return '1e';
    if (pos === 2) return '2e';
    if (pos === 3) return '3e';
    return `${pos}e`;
  };

  const getMedalColor = (pos) => {
    if (pos === 1) return 'text-yellow-500';
    if (pos === 2) return 'text-gray-400';
    if (pos === 3) return 'text-orange-600';
    return 'text-[#1a2332]';
  };

  const getBgGradient = (pos) => {
    if (pos === 1) return 'bg-gradient-to-r from-yellow-100 to-yellow-50 dark:from-yellow-900/50 dark:to-yellow-800/30 border-yellow-400 dark:border-yellow-600';
    if (pos === 2) return 'bg-gradient-to-r from-gray-200 to-gray-100 dark:from-gray-700/50 dark:to-gray-600/30 border-gray-400 dark:border-gray-500';
    if (pos === 3) return 'bg-gradient-to-r from-orange-100 to-orange-50 dark:from-orange-900/50 dark:to-orange-800/30 border-orange-400 dark:border-orange-600';
    return 'bg-card border-border';
  };

  const displayPayouts = showFullTable ? effectivePayouts : effectivePayouts.slice(0, 10);

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block bg-[#FDB913] rounded-full p-4 mb-4">
            <Trophy size={40} className="text-[#1a2332]" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-2">Prijzenstructuur</h1>
          <p className="text-muted-foreground">Hoe meer deelnemers, hoe meer prijswinnaars!</p>
        </div>

        {/* Entry Cost Info */}
        <Card className="mb-8 border-2 border-[#FDB913] dark:bg-[#0f1419]">
          <CardContent className="p-6 bg-gradient-to-br from-[#FDB913]/10 to-[#FDB913]/5 dark:from-[#1a2332] dark:to-[#0f1419]">
            <h2 className="text-2xl font-bold text-[#1a2332] dark:text-[#FDB913] mb-4 flex items-center">
              <Euro size={28} className="mr-2" />
              Deelnamekosten Seizoen 2026
            </h2>
            <div className={`grid grid-cols-1 gap-4 ${YEARLY_BONUS > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
              <div className="bg-[#1a2332] dark:bg-[#0a0f14] rounded-lg p-4 text-center border dark:border-gray-700">
                <p className="text-3xl font-bold text-[#FDB913]">€{COST_PER_RACE.toFixed(2).replace('.', ',')}</p>
                <p className="text-sm text-white/80">per race</p>
              </div>
              <div className="bg-[#1a2332] dark:bg-[#0a0f14] rounded-lg p-4 text-center border dark:border-gray-700">
                <p className="text-3xl font-bold text-white">× {RACES_2026} races</p>
                <p className="text-sm text-white/80">+ €{ADMIN_COST.toFixed(2).replace('.', ',')} administratiekosten</p>
              </div>
              {YEARLY_BONUS > 0 && (
                <div className="bg-purple-600 dark:bg-purple-800 rounded-lg p-4 text-center border dark:border-purple-600">
                  <p className="text-3xl font-bold text-white">€{YEARLY_BONUS.toFixed(2).replace('.', ',')}</p>
                  <p className="text-sm text-white/80">jaarbonus</p>
                </div>
              )}
              <div className="bg-[#FDB913] rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-[#1a2332]">€{TOTAL_ENTRY.toFixed(2).replace('.', ',')}</p>
                <p className="text-sm text-[#1a2332]">totaal per deelnemer</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Yearly Bonus Info - only show if bonus > 0 */}
        {YEARLY_BONUS > 0 && (
          <Card className="mb-8 border-2 border-purple-500 bg-gradient-to-r from-purple-500/10 to-purple-600/5 dark:from-purple-900/30 dark:to-purple-800/20">
            <CardContent className="p-6">
              <div className="flex items-center space-x-3 mb-3">
                <div className="bg-purple-500 rounded-full p-2">
                  <Trophy size={24} className="text-white" />
                </div>
                <h2 className="text-xl font-bold text-purple-600 dark:text-purple-400">Jaarbonus</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                Elke deelnemer betaalt €{YEARLY_BONUS.toFixed(2).replace('.', ',')} voor de Jaarbonus. 
                De speler met de <strong>hoogste score van het seizoen</strong> wint de Jaarbonus.
              </p>
              
              {/* Jaarbonus pot display */}
              <div className="bg-card rounded-lg p-4 border border-purple-300 dark:border-purple-700 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Jaarbonus pot bij {selectedParticipants} deelnemers:</span>
                  <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    €{(selectedParticipants * YEARLY_BONUS).toFixed(2).replace('.', ',')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  ({selectedParticipants} deelnemers × €{YEARLY_BONUS.toFixed(2).replace('.', ',')})
                </p>
              </div>

              {/* €449 Compliance warning if bonus exceeds max */}
              {(selectedParticipants * YEARLY_BONUS) > MAX_PAYOUT && (
                <div className="bg-amber-50 dark:bg-amber-900/30 rounded-lg p-4 border border-amber-400 dark:border-amber-600">
                  <div className="flex items-start space-x-3">
                    <Shield className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <h4 className="font-semibold text-amber-700 dark:text-amber-300 mb-2">
                        Gedoogbeleid Kansspelen (max €449)
                      </h4>
                      <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
                        Omdat de jaarbonus pot €{(selectedParticipants * YEARLY_BONUS).toFixed(2).replace('.', ',')} bedraagt 
                        (boven €{MAX_PAYOUT}), wordt de bonus verdeeld over meerdere winnaars:
                      </p>
                      
                      {/* Calculate how many winners needed */}
                      {(() => {
                        const totalBonus = selectedParticipants * YEARLY_BONUS;
                        const winnersNeeded = Math.ceil(totalBonus / MAX_PAYOUT);
                        const prizes = [];
                        let remaining = totalBonus;
                        
                        for (let i = 1; i <= winnersNeeded; i++) {
                          if (remaining <= MAX_PAYOUT) {
                            prizes.push({ position: i, amount: remaining });
                            remaining = 0;
                          } else {
                            prizes.push({ position: i, amount: MAX_PAYOUT });
                            remaining -= MAX_PAYOUT;
                          }
                        }
                        
                        return (
                          <div className="space-y-2">
                            {prizes.map((prize, idx) => (
                              <div key={idx} className="flex justify-between items-center bg-white/50 dark:bg-black/20 rounded px-3 py-2">
                                <span className="text-sm text-amber-700 dark:text-amber-300">
                                  {prize.position === 1 ? '1e prijs (hoogste seizoen score)' : 
                                   prize.position === 2 ? '2e prijs (op-één-na-hoogste)' :
                                   `${prize.position}e prijs (op-${prize.position - 1}-na-hoogste)`}
                                </span>
                                <span className="font-bold text-amber-700 dark:text-amber-300">
                                  €{prize.amount.toFixed(2).replace('.', ',')}
                                </span>
                              </div>
                            ))}
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 italic">
                              Bij gelijke scores wordt de prijs gelijk verdeeld tussen de deelnemers.
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Prize Pool Calculator */}
        <Card className="mb-8 border-2 border-blue-400">
          <CardHeader>
            <CardTitle className="flex items-center text-foreground">
              <Calculator size={24} className="mr-2 text-blue-500" />
              Prijzenpot Calculator
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-6">
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Aantal deelnemers: <span className="text-[#FDB913] font-bold text-xl">{selectedParticipants}</span>
              </label>
              <input
                type="range"
                min="2"
                max="2000"
                step="1"
                value={selectedParticipants}
                onChange={(e) => setSelectedParticipants(parseInt(e.target.value))}
                className="w-full h-3 bg-secondary rounded-lg appearance-none cursor-pointer accent-[#FDB913]"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>2</span>
                <span>200</span>
                <span>500</span>
                <span>1000</span>
                <span>1500</span>
                <span>2000</span>
              </div>
            </div>

            <div className="bg-[#1a2332] rounded-lg p-6 text-center border-2 border-green-500">
              <p className="text-sm text-green-400 mb-1">Totale Prijzenpot</p>
              <p className="text-5xl font-black text-green-400">€{prizePool.toLocaleString('nl-NL')}</p>
              <p className="text-xs text-green-500 mt-2">
                ({selectedParticipants} deelnemers × €{COST_PER_RACE * RACES_2026} inleg)
              </p>
            </div>

            <div className="mt-4 flex items-center justify-center space-x-2 text-sm text-muted-foreground">
              <Users size={16} />
              <span>
                Bij {selectedParticipants} deelnemers vallen <strong className="text-[#FDB913]">{effectiveWinners} spelers</strong> in de prijzen
              </span>
            </div>

            {/* Compliance Warning */}
            {complianceData && !complianceData.compliant && (
              <div className="mt-4 p-4 bg-orange-100 dark:bg-orange-900/30 border-2 border-orange-500 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Shield className="text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" size={24} />
                  <div>
                    <h4 className="font-bold text-orange-700 dark:text-orange-400">Gedoogbeleid Kansspelen (max €449)</h4>
                    <p className="text-sm text-orange-600 dark:text-orange-300 mt-1">
                      {complianceData.message || 'Aanpassing nodig om te voldoen aan het gedoogbeleid.'}
                    </p>
                    <div className="mt-2 text-sm bg-white/50 dark:bg-black/20 rounded p-2 space-y-1">
                      <div className="flex justify-between">
                        <span className="text-orange-600 dark:text-orange-300">Prijzenpot:</span>
                        <strong className="text-orange-700 dark:text-orange-400">
                          €{complianceData.prize_pool?.toFixed(2).replace('.', ',')}
                        </strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-orange-600 dark:text-orange-300">1e prijs ({complianceData.proposed_structure?.payouts?.[0]?.percentage}%):</span>
                        <strong className="text-orange-700 dark:text-orange-400">
                          €{complianceData.proposed_first_amount?.toFixed(2).replace('.', ',')}
                        </strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-orange-600 dark:text-orange-300">Laatste prijs (min €{complianceData.min_payout?.toFixed(2).replace('.', ',')}):</span>
                        <strong className="text-orange-700 dark:text-orange-400">
                          €{complianceData.proposed_last_amount?.toFixed(2).replace('.', ',')}
                        </strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-orange-600 dark:text-orange-300">Aantal prijswinnaars:</span>
                        <strong className="text-orange-700 dark:text-orange-400">{complianceData.proposed_winners}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payout Table */}
        <Card className="mb-8 border-2 border-[#1a2332]">
          <CardHeader className="bg-[#1a2332] text-white">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <Trophy size={24} className="mr-2 text-[#FDB913]" />
                Uitbetaling ({currentCategory} deelnemers)
              </span>
              <span className="text-[#FDB913] font-normal text-sm">
                Prijzenpot: €{prizePool.toLocaleString('nl-NL')}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-200">
              {displayPayouts.map((payout) => {
                const prizeAmount = (prizePool * payout.percentage / 100);
                return (
                  <div
                    key={payout.position}
                    className={`flex items-center justify-between p-4 ${getBgGradient(payout.position)} border-l-4`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`text-2xl font-bold ${getMedalColor(payout.position)}`}>
                        {payout.position <= 3 ? (
                          <Trophy size={28} className={getMedalColor(payout.position)} />
                        ) : (
                          <span className="w-7 inline-block text-center">{payout.position}</span>
                        )}
                      </div>
                      <div>
                        <p className="font-bold text-foreground">{getPositionLabel(payout.position)} Plaats</p>
                        <p className="text-sm text-muted-foreground">{payout.percentage}% van de pot</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${payout.position <= 3 ? getMedalColor(payout.position) : 'text-green-600 dark:text-green-400'}`}>
                        €{prizeAmount.toFixed(2).replace('.', ',')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {effectivePayouts.length > 10 && (
              <button
                onClick={() => setShowFullTable(!showFullTable)}
                className="w-full p-4 bg-secondary hover:bg-secondary/80 transition-colors flex items-center justify-center space-x-2 text-foreground font-medium"
              >
                {showFullTable ? (
                  <>
                    <ChevronUp size={20} />
                    <span>Minder tonen</span>
                  </>
                ) : (
                  <>
                    <ChevronDown size={20} />
                    <span>Alle {effectivePayouts.length} prijswinnaars tonen</span>
                  </>
                )}
              </button>
            )}
          </CardContent>
        </Card>

        {/* Quick Reference Table */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-foreground">
              <Info size={24} className="mr-2 text-blue-500" />
              Overzicht per Aantal Deelnemers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#1a2332] text-white">
                  <tr>
                    <th className="p-3 text-left">Deelnemers</th>
                    <th className="p-3 text-center">Prijswinnaars</th>
                    <th className="p-3 text-right">1e Plaats %</th>
                    <th className="p-3 text-right">Voorbeeld Pot</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {Object.entries(payoutStructure).map(([range, data]) => {
                    // Calculate example pot based on min of range
                    let exampleParticipants;
                    if (range === '1500-2000') exampleParticipants = 1500;
                    else if (range === '1000-1499') exampleParticipants = 1000;
                    else if (range === '500-999') exampleParticipants = 500;
                    else if (range === '200-499') exampleParticipants = 200;
                    else if (range === '150-199') exampleParticipants = 150;
                    else if (range === '100-149') exampleParticipants = 100;
                    else if (range === '80-99') exampleParticipants = 80;
                    else exampleParticipants = parseInt(range.split('-')[0]);
                    
                    const examplePot = exampleParticipants * COST_PER_RACE * RACES_2026;
                    return (
                      <tr key={range} className="hover:bg-secondary">
                        <td className="p-3 font-medium">{range}</td>
                        <td className="p-3 text-center text-[#FDB913] font-bold">{data.winners}</td>
                        <td className="p-3 text-right">{data.payouts[0]?.percentage || 0}%</td>
                        <td className="p-3 text-right text-green-600 font-medium">€{examplePot.toLocaleString('nl-NL')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Important Info */}
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
          <h3 className="font-bold text-blue-900 dark:text-blue-100 mb-3 text-lg flex items-center">
            <Info size={20} className="mr-2" />
            Belangrijke Informatie
          </h3>
          <ul className="space-y-2 text-blue-800 dark:text-blue-200 text-sm">
            <li>• <strong>Inleg:</strong> €{COST_PER_RACE.toFixed(2).replace('.', ',')} per race × {RACES_2026} races = €{(COST_PER_RACE * RACES_2026).toFixed(2).replace('.', ',')} + €{ADMIN_COST.toFixed(2).replace('.', ',')} administratiekosten{YEARLY_BONUS > 0 ? ` + €${YEARLY_BONUS.toFixed(2).replace('.', ',')} jaarbonus` : ''} = <strong>€{TOTAL_ENTRY.toFixed(2).replace('.', ',')} totaal</strong></li>
            <li>• <strong>Prijzenpot:</strong> 100% van de race-inleg (€{(COST_PER_RACE * RACES_2026).toFixed(2).replace('.', ',')} per deelnemer) gaat naar de prijzenpot</li>
            <li>• <strong>Administratiekosten:</strong> €{ADMIN_COST.toFixed(2).replace('.', ',')} per deelnemer voor organisatie en hosting</li>
            {YEARLY_BONUS > 0 && (
              <li>• <strong>Jaarbonus:</strong> €{YEARLY_BONUS.toFixed(2).replace('.', ',')} per deelnemer voor de speler met de hoogste seizoen score</li>
            )}
            <li>• <strong>Uitbetaling:</strong> Aan het einde van het seizoen na de laatste race</li>
            <li>• <strong>Meer deelnemers = Meer prijswinnaars:</strong> Van 1 winnaar (2-3 deelnemers) tot 300 winnaars (1500-2000 deelnemers)</li>
            <li>• <strong>Geen verborgen kosten:</strong> Alle prijzen worden volledig uitgekeerd</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Prijzen;
